import * as H from 'history'
import { action } from 'mobx'
import { Container, Singleton } from 'typescript-ioc/es5'
import axios from 'axios'
import { AppState } from '@halo/stores/app.state'
import querystring from 'query-string'
import { DomainDto } from '@halo/dtos/domain.dto'
import * as CryptoJS from 'crypto-js'
import { simpleNotification } from '@halo/_utils/notifications'
import { IApiResponse, IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { axiosFetch, isAxiosCancellation } from '@halo/_utils/axios'
import aqe from '@pushly/aqe'
import { FlagModel } from '@halo/models/flag.model'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { IApiKeyCreateDto } from '@halo/components/data-form-elements/api-settings-well/internal-api-settings-well'
import { ApiKeyDto } from '@halo/dtos/user-with-key.dto'

@Singleton
export class AppService {
    public appContainerRef: any
    public contentContainerRef: any
    public platformMessageTimer: number

    protected appState: AppState
    protected historyContainer: H.History
    protected previousRoute: string
    protected fetchingPlatformMessages: boolean = false

    public constructor() {
        this.appState = Container.get(AppState)
    }

    public set404State() {
        this.appState.showing404Count = this.appState.showing404Count + 1
    }

    public unset404State() {
        this.appState.showing404Count = this.appState.showing404Count <= 0 ? 0 : this.appState.showing404Count - 1
    }

    public reset404State() {
        this.appState.showing404Count = 0
    }

    public resetContentScrollPosition(): void {
        if (!!this.contentContainerRef) {
            this.contentContainerRef.scrollTo(0, 0)
        }
    }

    public getDomainDto(id: number) {
        return this.appState.currentUserDomains?.find((d) => d.id === id)
    }

    public getAppContainer = (): HTMLElement => {
        return this.appContainerRef
    }

    public attachHistoryContainer(container: any): void {
        if (!this.historyContainer) {
            this.historyContainer = container
        }
    }

    public get currentRoute(): string {
        return this.historyContainer.location.pathname
    }

    @action.bound
    public route(newRoute: string, state?: any): void {
        this.previousRoute = this.currentRoute
        this.historyContainer.push(newRoute, state)
    }

    @action.bound
    public routeBack(): void {
        const newRoute = this.previousRoute
        if (!newRoute || newRoute === this.currentRoute) {
            this.historyContainer.goBack()
            return
        }
        this.route(newRoute)
    }

    @action.bound
    public setAppLoading(): void {
        this.appState.loadingCount = this.appState.loadingCount + 1
    }

    @action.bound
    public unsetAppLoading(): void {
        if (this.appState.loadingCount === 0) {
            return
        }

        this.appState.loadingCount = this.appState.loadingCount - 1
    }

    @action.bound
    public setModuleLoading(): void {
        this.appState.moduleLoadingCount = this.appState.moduleLoadingCount + 1
    }

    @action.bound
    public unsetModuleLoading(): void {
        if (this.appState.moduleLoadingCount === 0) {
            return
        }

        this.appState.moduleLoadingCount = this.appState.moduleLoadingCount - 1
    }

    @action.bound
    public addAndSetCurrentUserDomain(domain: DomainDto) {
        this.appState.currentUserDomains = [...(this.appState.currentUserDomains || []), domain]

        this.appState.currentDomainJsonData = JSON.stringify(domain)
        // setCurrentDomainSession(domain);
        this.updateDomainInPlatformPath(domain)
    }

    @action.bound
    public updateDomain(domain: DomainDto) {
        if (this.appState.currentUserDomains) {
            const index = this.appState.currentUserDomains.findIndex((d) => d.id === domain.id)
            this.appState.currentUserDomains.splice(index, 1, domain)
        }
    }

    @action.bound
    public removeDomain(domain: DomainDto) {
        if (this.appState.currentUserDomains) {
            const index = this.appState.currentUserDomains.findIndex((d) => d.id === domain.id)
            this.appState.currentUserDomains.splice(index, 1)
        }
    }

    public get domainIdFromPath(): string | undefined {
        let id: any
        const idRequestRegx = /.*?\/domains\/([\d]+).*/i

        if (this.historyContainer) {
            const path = this.historyContainer.location.pathname
            if (idRequestRegx.test(path)) {
                id = path.replace(/.*?\/domains\/([\d]+).*/i, '$1')
            }
        }

        return id
    }

    public currentUserCanReadAtLeastOneOrg = () => {
        const allUserOrgIds = this.appState.currentUserDomains?.map((d) => d.account_id) ?? []
        return (
            allUserOrgIds.length > 0 &&
            allUserOrgIds.some((id) =>
                this.appState.abilityStore.can(
                    AbilityAction.READ,
                    // all returned orgs are guaranteed to have "{ statusId: 1 }"
                    // if an external user is logged in. Internal usrs ignore status check
                    asCaslSubject(SubjectEntity.ORG, { id, statusId: 1 }),
                ),
            )
        )
    }

    @action.bound
    public toggleSidebar() {
        this.appState.sidebar.collapsed = !this.appState.sidebar.collapsed
    }

    @action.bound
    public closeSidebar() {
        this.appState.sidebar.collapsed = true
    }

    @action.bound
    public openSidebar() {
        this.appState.sidebar.collapsed = false
    }

    public async fetchPlatformMessages(opts?: IApiCallOptions): Promise<IApiResponse<any[]>> {
        const options = querystring.stringify(opts?.query || {})
        let ok = true
        let meta = {}
        let data: any[] = []
        let cancelled = false
        let error

        try {
            const req = await axios.get(`${aqe.defaults.publicApiDomain}/v3/system/messages?${options}`)
            const { data: messages, ...metaData } = req.data
            data = messages
        } catch (err) {
            cancelled = isAxiosCancellation(err)
            error = err
            ok = false
        }
        return { ok, meta, data, cancelled, error }
    }

    public async createMessage(msg: any): Promise<boolean> {
        this.setModuleLoading()

        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/system/messages`, msg)
            simpleNotification('success', `Message was successfully created`)
            this.unsetModuleLoading()
            return true
        } catch (error) {
            simpleNotification('error', error.response.data.message)
            this.unsetModuleLoading()
            return false
        }
    }

    public async updateMessage(msg: any, id: number): Promise<boolean> {
        this.setModuleLoading()

        try {
            await axios.patch(`${aqe.defaults.publicApiDomain}/system/messages/${id}`, msg)
            simpleNotification('success', `Message successfully updated.`)

            this.unsetModuleLoading()
            return true
        } catch (error) {
            simpleNotification('error', error.response.data.message)
            this.unsetModuleLoading()
            return false
        }
    }

    public async destroyMessage(msg: any): Promise<boolean> {
        this.setModuleLoading()

        try {
            await axios.delete(`${aqe.defaults.publicApiDomain}/system/messages/${msg.id}`)
            // simpleNotification('success', `Message was successfully removed`);
            this.unsetModuleLoading()
            return true
        } catch (error) {
            // simpleNotification('error', error.response.data.message);
            this.unsetModuleLoading()
            return false
        }
    }

    public async fetchFlags(opts?: IApiCallOptions): Promise<any> {
        const options = querystring.stringify(opts?.query || {})
        let ok = true
        let meta = {}
        let data: any[] = []
        let cancelled = false
        let error

        try {
            const req = await axios.get(`${aqe.defaults.publicApiDomain}/v3/flags?${options}`)

            const { data: flags, ...metaData } = req.data
            data = flags.map((flag: any) => FlagModel.build(flag))

            meta = metaData
        } catch (err) {
            cancelled = isAxiosCancellation(err)
            error = err
            ok = false
        }
        return { ok, meta, data, cancelled, error }
    }

    public async createFlag(flag: any, opts?: IApiCallOptions): Promise<any> {
        if (opts?.showLoadingScreen) this.setModuleLoading()

        const options = querystring.stringify(opts?.query || {})
        let ok = true

        try {
            const res = await axios.post(`${aqe.defaults.publicApiDomain}/flags?${options}`, flag)

            flag = res.data
            simpleNotification(
                'success',
                `Flag was successfully created${opts.query?.applyAll ? ' and all defaults were applied.' : '.'}`,
            )
        } catch (error) {
            if (opts?.showLoadingScreen) this.unsetModuleLoading()
            ok = false
            simpleNotification('error', error.response.data.message)
        }

        if (opts?.showLoadingScreen) this.unsetModuleLoading()
        return { ok, flag }
    }

    public async updateFlag(flag: any, key: string, opts: IApiCallOptions = {}): Promise<any> {
        if (opts?.showLoadingScreen) this.setModuleLoading()

        let ok = true

        try {
            const res = await axios.patch(`${aqe.defaults.publicApiDomain}/flags/${key}`, flag)

            flag = res.data
            simpleNotification(
                'success',
                `Flag was successfully updated${opts?.query?.applyAll ? ' and all defaults were applied.' : '.'}`,
            )
        } catch (error) {
            if (opts?.showLoadingScreen) this.unsetModuleLoading()
            ok = false
            simpleNotification('error', error.response.data.message)
        }

        if (opts?.showLoadingScreen) this.unsetModuleLoading()
        return { ok, flag }
    }

    public async archiveFlag(flagKey: string, opts?: IApiCallOptions): Promise<boolean> {
        if (opts?.showLoadingScreen) this.setModuleLoading()

        let ok = true

        try {
            const res = await axios.delete(`${aqe.defaults.publicApiDomain}/flags/${flagKey}`)

            if (res) {
                simpleNotification('success', 'Flag was successfully archived.')
            }
        } catch (error) {
            if (opts?.showLoadingScreen) this.unsetModuleLoading()

            ok = false
            simpleNotification('error', error.response.data.message)
        }
        if (opts?.showLoadingScreen) this.unsetModuleLoading()

        return ok
    }

    public async getFlagFilters(): Promise<any> {
        try {
            const filters = await axios.get(`${aqe.defaults.publicApiDomain}/v3/flags/filters`)

            return filters.data.data
        } catch (error) {
            simpleNotification('error', error.response.data.message)
            return false
        }
    }

    public async getFilterOpts(): Promise<any> {
        try {
            const filters = await axios.get(`${aqe.defaults.publicApiDomain}/v3/system/messages/filters`)

            this.unsetModuleLoading()
            return filters.data.data
        } catch (error) {
            simpleNotification('error', error.response.data.message)
            return false
        }
    }

    public async calculateMediaHash(url: string): Promise<string> {
        const file = await fetch(url)
        const blob = await file.blob()

        const hash: any = await new Promise((res) => {
            const fileReader = new FileReader()

            fileReader.onload = (e: any) => {
                const data = CryptoJS.SHA256(CryptoJS.enc.Latin1.parse(e.target.result))
                res(data)
            }

            fileReader.readAsBinaryString(blob)
        })

        return hash.toString()
    }

    public async checkStoredMediaUrl(url: string, entityType: 'ACCOUNT' | 'DOMAIN', entityId: number): Promise<any> {
        const response: any = { url }

        try {
            response.hash = await this.calculateMediaHash(url)

            const entityPath = `${entityType.toLowerCase()}s/${entityId}`
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/${entityPath}/media/${response.hash}`)
            response.data = res.data.data
        } catch {}

        return response
    }

    public async postStoredMediaUrl(
        originalUrl: string,
        originalHash: string,
        modifiedUrl: string,
        entityType: 'ACCOUNT' | 'DOMAIN',
        entityId: number,
    ): Promise<any> {
        try {
            const modifiedHash = await this.calculateMediaHash(modifiedUrl)
            const entityPath = `${entityType.toLowerCase()}s/${entityId}`

            await axios.post(`${aqe.defaults.publicApiDomain}/${entityPath}/media`, {
                originalMediaUrl: originalUrl,
                originalMediaHash: originalHash,
                modifiedMediaUrl: modifiedUrl,
                modifiedMediaHash: modifiedHash,
                ownerEntityType: entityType,
                ownerEntityId: entityId,
            })
        } catch (error) {
            simpleNotification('error', error.response.data.message)
        }
    }

    public async removeMediaPartial(handle: string): Promise<any> {
        try {
            const res = await axios.delete(`${aqe.defaults.publicApiDomain}/media/partial/${handle}`)
            return res.data
        } catch (error) {
            simpleNotification('error', error.response.data.message)
        }
    }

    public async getApplicationVersions() {
        try {
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/v3/system/application-versions`)
            return res.data.data
        } catch (error) {
            simpleNotification('error', error.response.data.message)
        }
    }

    public async getEventLogs(opts: any = {}, showLoadingScreen: boolean = false): Promise<any> {
        if (showLoadingScreen) this.setModuleLoading()
        let res: any = { data: [], total: 0, count: 0 }

        try {
            const options = querystring.stringify(opts || {})
            const serviceURL = `${aqe.defaults.publicApiDomain}/system/event-logs${options}`

            res = await axios.get(serviceURL)
            if (showLoadingScreen) this.unsetModuleLoading()

            return res.data
        } catch (error) {
            // simpleNotification('error', error.response.data.message);
            if (showLoadingScreen) this.unsetModuleLoading()

            return res
        }
    }

    public async createApiKey(
        createDto: IApiKeyCreateDto,
        opts?: IApiCallOptions,
    ): Promise<IApiResponse<ApiKeyDto | null>> {
        let response: IApiResponse<ApiKeyDto | null> = {
            ok: false,
            data: null,
        }

        if (opts?.showLoadingScreen) {
            this.setModuleLoading()
        }

        const serviceUrl = `${aqe.defaults.publicApiDomain}/system/api-keys`

        try {
            const req = await axiosFetch('post', {
                url: serviceUrl,
                data: createDto,
            })

            const { data } = req.data

            response.ok = true
            response.data = data
        } catch (error) {
            response.error = error
            response.cancelled = isAxiosCancellation(error)

            if (!response.cancelled) {
                simpleNotification('error', error.response?.data?.message)
            }
        }

        if (opts?.showLoadingScreen) {
            this.unsetModuleLoading()
        }

        return response
    }

    public async archiveApiKey(apiKeyId: string, opts?: IApiCallOptions): Promise<IApiResponse<boolean>> {
        let response: IApiResponse<boolean> = {
            ok: false,
            data: false,
        }

        if (opts?.showLoadingScreen) {
            this.setModuleLoading()
        }

        const serviceUrl = `${aqe.defaults.publicApiDomain}/system/api-keys/${apiKeyId}`

        try {
            const req = await axiosFetch('delete', {
                url: serviceUrl,
            })

            const { data } = req.data
            response.ok = true
            response.data = data

            simpleNotification('success', 'API Key was successfully archived.')
        } catch (error) {
            response.error = error
            response.cancelled = isAxiosCancellation(error)

            if (!response.cancelled) {
                simpleNotification('error', error.response?.data?.message)
            }
        }

        if (opts?.showLoadingScreen) {
            this.unsetModuleLoading()
        }

        return response
    }

    protected updateDomainInPlatformPath(domain: DomainDto): void {
        const currentDomainPath = this.currentRoute
        const newDomainPath = currentDomainPath.replace(/\/?domains\/[\d]+\/?/i, `/domains/${domain.id}/`)
        if (newDomainPath !== currentDomainPath) {
            this.route(newDomainPath)
        }
    }
}
